/**
 * Feature-to-config-slot assembler: merges feature-provided values into a
 * structure's config slots in priority order.
 *
 * Structures declare config slots; features plug values into those slots when
 * they're enabled. `assembleConfig` walks the active feature list, collects
 * each feature's `providesConfig` contributions, and dedupes by
 * `key`/`priority` (higher priority wins). Two array helpers change the merge
 * semantics: `REPLACE` overrides whatever was in the slot rather than extending
 * it, and `FIRST` prepends rather than appends. Together with `dedupable` for
 * non-array slots, these are the levers features use to override defaults from
 * structures or other features without forking the structure file.
 */

import type { RouterUtils } from '@orpc/react-query';
import type { IsoLocale } from '@repo/shared/locale';
import type { UseSuspenseQueryOptions } from '@tanstack/react-query';
import type { FeatureMenuItem } from 'feature/feature-helpers';
import type { ApiClient } from 'system/orpc';
import { EMPTY_OBJECT, type Feature, type Structure } from 'util/stdtypes';
import type { StructureModule } from 'util/structure-module';

import { getDeploymentConfig } from './util';

type EnabledFeaturesMap = Record<string, boolean>;

interface DedupableConfigItem {
    key?: string;
    priority?: number;
}

// This feature is replacing the config items already present rather than
// extending them

// HACK: We cast it to T[] despite the fact that it's not a T[] because
// otherwise all configs would have to use a custom array type, which would be a
// pain, and anything that comes back from useConfig will have gone through
// assembleConfig which converts all the REPLACE arrays back into regular
// arrays.
export function REPLACE<T>(array: T[]): T[] {
    return { REPLACE: array } as any as T[];
}

// Add these items to the front of the array of items rather than the back
export function FIRST<T>(array: T[]): T[] {
    return { FIRST: array } as any as T[];
}

// helper to make any value (component or function) de-dupable by key and
// priority when multiple features provide the same config slot, the one with
// higher priority wins
export function dedupable<T extends object>(value: T, key: string, priority: number = 0): T & { key: string; priority: number } {
    return Object.assign(value, { key, priority });
}

// Get deployment config default features for a specific silo and structure.
// These are silo-specific overrides that sit between module defaults and stored
// defaults.
export function getDeploymentDefaultFeatures(siloKey: string | null, structureKey: string): EnabledFeaturesMap {
    if (!siloKey) return EMPTY_OBJECT;
    return getDeploymentConfig()?.defaultFeatures?.[siloKey]?.[structureKey] ?? EMPTY_OBJECT;
}

/**
 * The features on for an instance: its structure's defaults, overridden by its
 * deployment's defaults, overridden by its own toggles.
 */
export function getEnabledFeatures({
    siloKey,
    structureKey,
    structureModule,
    activeFeatures,
}: Pick<InstanceConfig, 'siloKey' | 'structureKey' | 'structureModule' | 'activeFeatures'>): EnabledFeaturesMap {
    return { ...structureModule.defaultFeatures, ...getDeploymentDefaultFeatures(siloKey, structureKey), ...activeFeatures };
}

export function noDefaultFeatures(defaultConfig: EnabledFeaturesMap): EnabledFeaturesMap {
    const disabledMap: Record<string, boolean> = {};
    Object.keys(defaultConfig).forEach((key) => {
        disabledMap[key] = false;
    });
    return disabledMap;
}

export function assembleConfig({
    structure,
    features,
    defaultConfig,
    activeFeatures,
    includeDefaults = true,
}: {
    structure: Structure<any>;
    features: FeatureMenuItem[];
    defaultConfig: EnabledFeaturesMap;
    activeFeatures: EnabledFeaturesMap | null | undefined;
    includeDefaults?: boolean;
}) {
    let config = cloneConfig(structure.receivesConfigDefaults);
    const defaultFeatures = includeDefaults ? { ...defaultConfig } : {};
    const mergedActiveFeatures = { ...defaultFeatures, ...activeFeatures };
    const availableFeatures = flattenFeatureBlocks(features, mergedActiveFeatures);

    try {
        availableFeatures.forEach((feature) => {
            if (feature.parentFeature && !mergedActiveFeatures[feature.parentFeature]) return;
            if (mergedActiveFeatures[feature.key]) {
                Object.keys(feature.providesConfig ?? {}).forEach((key) => {
                    const featureParam = feature.providesConfig?.[key];
                    const baseArray = config[key] ?? [];
                    if (Array.isArray(featureParam)) {
                        config[key] = dedupConfig([...baseArray, ...featureParam]);
                    } else if (featureParam && featureParam.FIRST) {
                        config[key] = dedupConfig([...featureParam.FIRST, ...baseArray]);
                    } else if (featureParam && featureParam.REPLACE) {
                        config[key] = featureParam.REPLACE;
                    } else if (featureParam !== undefined) {
                        // support de-duping for non-array values with
                        // key/priority
                        const existingPriority = (config[key] as DedupableConfigItem)?.priority ?? 0;
                        const newPriority = (featureParam as DedupableConfigItem)?.priority ?? 0;
                        if (newPriority >= existingPriority) {
                            config[key] = featureParam;
                        }
                    }
                });
                if (feature.receivesConfigDefaults) {
                    config = { ...config, ...feature.receivesConfigDefaults };
                }
            }
        });

        return config;
    } catch (e) {
        console.error('Error assembling config, using defaults', e);
        return cloneConfig(structure.receivesConfigDefaults);
    }
}

/**
 * The type every feature source's query options conform to: one shape a
 * single unconditional suspense query can take, whichever source built it.
 * The raw fetched shape differs per source (a space, a story, or nothing at
 * all), but each source's `select` narrows it to the instance's feature map.
 * The map is total: an instance that does not exist, or is hidden from the
 * caller, reads as the empty map, so every identity has a configuration — the
 * default one. Whether an instance exists is a question for its own data
 * reads, not for its feature toggles.
 */
export type FeaturesQueryOptions = UseSuspenseQueryOptions<any, any, Record<string, boolean>>;

/**
 * A feature source: builds the query options for one instance's active
 * feature toggles from the instance's identity. A mount hands over the whole
 * identity in one shape; a source that reads only part of it declares the
 * subset it uses, which a full identity always satisfies.
 *
 * The identity carries the reading locale because the two sources that fetch
 * read a resource the rest of the app also reads (`spaces.get`,
 * `stories.get`), and a query key is matched exactly — a source that dropped
 * it would fetch the same space twice and patch the entry nobody renders.
 */
export type FeaturesSource = (
    utils: RouterUtils<ApiClient>,
    identity: { siloKey: string; structureKey: string; instanceKey: string; locale: IsoLocale },
) => FeaturesQueryOptions;

/**
 * Everything a `Datastore` mount needs: the instance's identity, its resolved
 * structure module and active feature toggles, and the `config`/`screenSet`
 * pair assembled from them.
 */
export type InstanceConfig = {
    siloKey: string;
    structureKey: string;
    instanceKey: string;
    structureModule: StructureModule;
    activeFeatures: Record<string, boolean>;
    config: Record<string, any>;
    screenSet: Record<string, any>;
};

/**
 * Assembles the `config` and `screenSet` pair a `Datastore` mount needs, from
 * a structure module, an instance's active feature toggles, and the silo it
 * lives in.
 *
 * The one helper every mount site shares: a route layout's `useInstanceConfig`,
 * and the harness mounts (`TestDatastore`, the demo
 * story-set harnesses) that assemble their own instead of taking a config
 * override.
 */
export function assembleInstanceConfig({
    structureModule,
    siloKey,
    structureKey,
    activeFeatures,
}: {
    structureModule: StructureModule;
    siloKey: string;
    structureKey: string;
    activeFeatures: EnabledFeaturesMap;
}): { config: Record<string, any>; screenSet: Record<string, any> } {
    const { structure, features } = structureModule;
    const defaultConfig = { ...structureModule.defaultFeatures, ...getDeploymentDefaultFeatures(siloKey, structureKey) };
    return {
        config: assembleConfig({ structure, features, defaultConfig, activeFeatures }),
        screenSet: assembleScreenSet({ structure, features, defaultConfig, activeFeatures }),
    };
}

function dedupConfig(configArray: DedupableConfigItem[]) {
    const seen = new Map<string, DedupableConfigItem>();
    configArray.forEach((item) => {
        if (!item?.key) return;
        const key = item?.key;
        if (!seen.has(key)) {
            seen.set(key, item);
        } else {
            const existingPriority = seen.get(key)?.priority || 0;
            const priority = item?.priority || 0;
            if (priority > existingPriority) {
                seen.set(key, item);
            }
        }
    });

    // we reiterate over the original array to preserve order and keyless items,
    // that would not be in the Map.
    return configArray.filter((item) => {
        if (!item?.key) return true;
        return seen.get(item.key) === item;
    });
}

function assembleScreenSet({
    structure,
    features,
    defaultConfig,
    activeFeatures,
}: {
    structure: Structure<any>;
    features: FeatureMenuItem[];
    defaultConfig: EnabledFeaturesMap;
    activeFeatures: EnabledFeaturesMap | null | undefined;
}) {
    const mergedActiveFeatures = { ...defaultConfig, ...activeFeatures };
    const availableFeatures = flattenFeatureBlocks(features, mergedActiveFeatures);

    let screenSet = { ...structure.subscreens };
    availableFeatures.forEach((feature) => {
        if (mergedActiveFeatures[feature.key] && feature.subscreens) {
            screenSet = { ...screenSet, ...feature.subscreens };
        }
    });
    return screenSet;
}

function cloneConfig(config: Record<string, any> | undefined): Record<string, any> {
    if (!config) return {};
    const cloned = { ...config };
    Object.keys(cloned).forEach((key) => {
        if (Array.isArray(cloned[key])) {
            cloned[key] = [...cloned[key]];
        }
    });
    return cloned;
}

function flattenFeatureBlocks(
    featureList: FeatureMenuItem[],
    mergedActiveFeatures: Record<string, boolean> | null | undefined = null,
): Feature[] {
    const flattened: Feature[] = [];

    featureList.forEach((feature) => {
        if (!feature) {
            console.error('feature is null', featureList);
            return;
        }

        if ('kind' in feature) {
            switch (feature.kind) {
                case 'composite':
                    flattened.push(feature.parent);
                    if (!mergedActiveFeatures || mergedActiveFeatures[feature.parent.key]) {
                        flattened.push(...flattenFeatureBlocks(feature.features, mergedActiveFeatures));
                    }
                    break;

                case 'section':
                case 'chooseOne':
                    flattened.push(...flattenFeatureBlocks(feature.features, mergedActiveFeatures));
                    break;
            }
        } else {
            flattened.push(feature);
        }
    });

    return flattened;
}
